import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

/** En qué momento del flujo se pregunta. */
export type ContextoScreening = 'REGISTRO' | 'ENVIO';

/**
 * El veredicto. A propósito NO trae el nivel de riesgo ni el motivo: decirle
 * al cliente por qué se marcó su wallet es enseñarle qué evitar la próxima
 * vez. El detalle vive en el panel del operador.
 */
export interface ScreeningResultado {
  permitido: boolean;
  requiereRevision: boolean;
  mensaje: string | null;
  referencia: string;
}

/** Una fila del historial, como la devuelve el panel de operador. */
export interface ScreeningRegistro {
  id: string;
  userId: number;
  walletAddress: string;
  network: string;
  contexto: 'REGISTRO' | 'ENVIO';
  risk: string | null;
  decision: 'PERMITIDO' | 'BLOQUEADO' | 'REVISION' | 'ERROR';
  riskReason: string | null;
  clusterCategory: string | null;
  clusterName: string | null;
  error: string | null;
  referenciaId: string | null;
  desdeCache: number;
  createdAt: string;
}

export interface ScreeningHistorial {
  items: ScreeningRegistro[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Filtros del historial. */
export interface ScreeningFiltros {
  userId?: number;
  wallet?: string;
  decision?: string;
  contexto?: string;
  page?: number;
  limit?: number;
}

/**
 * Prevalidación de direcciones cripto contra el backend Nest.
 *
 * Esto NO reemplaza a nadie: el alta de beneficiarios la sigue resolviendo la
 * lambda de siempre y el envío sigue yendo al mismo endpoint. Acá solo se
 * pregunta antes, para poder avisarle al cliente y apagar el botón en vez de
 * dejarlo mandar una operación que va a rebotar.
 */
@Injectable({ providedIn: 'root' })
export class CryptoScreeningService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  /**
   * Verifica una dirección.
   *
   * Si la consulta falla —el backend no responde, no hay sesión, lo que sea—
   * devuelve permitido. Es deliberado: esta llamada es una prevalidación, no
   * el candado. Cortarle el alta de un beneficiario a un cliente porque un
   * servicio externo tuvo un mal día sería romper algo que hoy funciona, y en
   * el envío el backend vuelve a verificar por su cuenta antes de mover plata.
   */
  async verificar(
    walletAddress: string,
    network: string,
    contexto: ContextoScreening,
  ): Promise<ScreeningResultado> {
    try {
      const headers = await this.getHeaders();
      const respuesta = await firstValueFrom(
        this.http.post<{ success: boolean; data: ScreeningResultado }>(
          `${this.baseUrl}crypto-wallet-screening`,
          { walletAddress, network, contexto },
          { headers },
        ),
      );

      const data = respuesta?.data;
      if (!data || typeof data.permitido !== 'boolean') {
        return this.permitirPorDefecto();
      }
      return data;
    } catch (error) {
      console.warn('[screening] no se pudo verificar la wallet', error);
      return this.permitirPorDefecto();
    }
  }

  private permitirPorDefecto(): ScreeningResultado {
    return {
      permitido: true,
      requiereRevision: false,
      mensaje: null,
      referencia: '',
    };
  }

  /**
   * Consulta puntual de una dirección contra Chainalysis, para el operador.
   *
   * Esta SÍ pega al proveedor y se paga. Va solo bajo demanda, nunca en la
   * carga de una lista.
   */
  async consultarRiesgo(address: string): Promise<any> {
    const headers = await this.getHeaders();
    const respuesta = await firstValueFrom(
      this.http.get<{ success: boolean; data: any }>(
        `${this.baseUrl}chainalysis/screening/${encodeURIComponent(
          address.trim(),
        )}`,
        { headers },
      ),
    );
    return respuesta?.data;
  }

  /** Historial de decisiones ya tomadas. No consulta al proveedor. */
  async historial(filtros: ScreeningFiltros): Promise<ScreeningHistorial> {
    const headers = await this.getHeaders();

    let params = new HttpParams();
    Object.entries(filtros).forEach(([clave, valor]) => {
      if (valor !== undefined && valor !== null && `${valor}`.trim() !== '') {
        params = params.set(clave, `${valor}`);
      }
    });

    const respuesta = await firstValueFrom(
      this.http.get<ScreeningHistorial & { success: boolean }>(
        `${this.baseUrl}crypto-wallet-screening-control`,
        { headers, params },
      ),
    );

    return {
      items: respuesta?.items || [],
      total: respuesta?.total || 0,
      page: respuesta?.page || 1,
      limit: respuesta?.limit || 25,
      totalPages: respuesta?.totalPages || 1,
    };
  }

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }
}
